import PropTypes from "prop-types";
import * as React from "react";
import { useHistory } from "react-router-dom";
import {
  SHOP_ROUTE_RULE_MESSAGE,
  finalizeShopRouteInput,
  isShopRoute,
  normalizeShopRouteInput,
  settleShopRouteInput,
} from "../../../domain/shopRoute";
import { useCurrentUser } from "../../../globalState";
import { useInfluencer } from "../../../hooks";
import {
  Button,
  Chip,
  Textarea,
  TextInput,
  Typography,
} from "../../../themeV2";
import { dashboardPath } from "../../../domain/dashboardPaths";
import { updateInfluencer } from "../../Dashboard/AdvancedSettings/service";
import styles from "./ShopSettingsDialog.module.css";

const ProfileContent = React.memo(function ProfileContent({
  route,
  influencer: parentInfluencer,
  onClose,
}) {
  const history = useHistory();
  const currentUser = useCurrentUser();
  const {
    influencer: fullInfluencer,
    isLoading,
    isError,
    refresh,
  } = useInfluencer({ route, forShopBuilder: true });

  const influencer = fullInfluencer ?? parentInfluencer;

  const [description, setDescription] = React.useState(
    influencer?.caption ?? "",
  );
  const [name, setName] = React.useState(influencer?.name ?? "");
  const [shopRoute, setShopRoute] = React.useState(influencer?.route ?? "");
  const [nameError, setNameError] = React.useState(false);
  const [routeError, setRouteError] = React.useState("");
  const [errorText, setErrorText] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (!fullInfluencer) return;
    setDescription(fullInfluencer.caption ?? "");
    setName(fullInfluencer.name ?? "");
    setShopRoute(fullInfluencer.route ?? "");
  }, [fullInfluencer]);

  const unsaved = React.useMemo(() => {
    const descChanged = (description ?? "") !== (influencer?.caption ?? "");
    const nameChanged =
      currentUser.isAdmin && name !== (influencer?.name ?? "");
    const routeChanged =
      currentUser.isAdmin && shopRoute !== (influencer?.route ?? "");
    return descChanged || nameChanged || routeChanged;
  }, [description, name, shopRoute, influencer, currentUser.isAdmin]);

  const clearErrors = React.useCallback(() => {
    setNameError(false);
    setRouteError("");
    setErrorText("");
  }, []);

  const handleSave = async () => {
    if (isSaving) return;
    clearErrors();

    const routeChanged = shopRoute !== (influencer?.route ?? "");
    const nextRoute = routeChanged
      ? finalizeShopRouteInput(shopRoute)
      : shopRoute;

    if (currentUser.isAdmin) {
      if (!name) setNameError(true);
      if (!shopRoute) setRouteError("Shop link is required");
      if (!name || !shopRoute) {
        setErrorText("Cannot remove shop name or shop link");
        return;
      }
      if (routeChanged && !isShopRoute(nextRoute)) {
        setRouteError(SHOP_ROUTE_RULE_MESSAGE);
        return;
      }
    }

    setIsSaving(true);

    try {
      const identityPayload = {
        caption: description?.trim() ?? "",
        ...(currentUser.isAdmin ? { name: name.trim(), route: nextRoute } : {}),
      };

      const saved = await updateInfluencer(
        influencer.influencerId,
        identityPayload,
      );

      onClose();
      if (saved?.new_route) {
        history.push(dashboardPath({ shop: saved.new_route, page: "builder" }));
        return;
      }
      refresh();
    } catch (err) {
      console.error(err);
      setErrorText(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || isError) return null;

  return (
    <div>
      <div className={styles.fieldGroup}>
        {/* Shop Identity (admin only) */}
        {currentUser.isAdmin && (
          <>
            <TextInput
              label="Shop Name"
              required
              error={nameError ? "Shop name is required" : undefined}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearErrors();
              }}
              maxLength={40}
              disabled={isSaving}
            />
            <TextInput
              label="Shop Link"
              required
              error={routeError || undefined}
              value={shopRoute}
              onChange={(e) => {
                setShopRoute(normalizeShopRouteInput(e.target.value));
                clearErrors();
              }}
              onBlur={(e) =>
                setShopRoute(
                  settleShopRouteInput(influencer?.route)(e.target.value),
                )
              }
              description={`streamily.com/${shopRoute || "<username>"}`}
              disabled={isSaving}
            />
          </>
        )}

        {/* Shop Description */}
        <Textarea
          label="Shop Description"
          description="A short bio shown on your storefront"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          autoGrow
          rows={4}
          disabled={isSaving}
        />
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerStatus}>
          {unsaved && <Chip label="Unsaved" size="sm" aria-live="polite" />}
          {errorText && (
            <Typography variant="label-md" color="error" as="span">
              {errorText}
            </Typography>
          )}
        </div>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          loading={isSaving}
          style={{ minWidth: 120 }}
        >
          Save
        </Button>
      </div>
    </div>
  );
});

ProfileContent.propTypes = {
  route: PropTypes.string.isRequired,
  influencer: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ProfileContent;
